import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Copy,
  Download,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  AlertTriangle,
  ArrowUpRight,
  History,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Duplicates = () => {
  const [duplicates, setDuplicates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  // Filters
  const [duplicateType, setDuplicateType] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ states: [] });

  const fetchDuplicates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "50",
      });

      if (duplicateType) params.append("duplicate_type", duplicateType);
      if (selectedState) params.append("state", selectedState);

      const response = await fetch(`${API}/duplicates?${params}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setDuplicates(data.duplicates);
        setTotal(data.total);
        setPages(data.pages);
      }
    } catch (error) {
      console.error("Failed to fetch duplicates:", error);
      toast.error("Failed to load duplicates");
    } finally {
      setLoading(false);
    }
  }, [page, duplicateType, selectedState]);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await fetch(`${API}/duplicates/stats`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await fetch(`${API}/filters/options`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setFilterOptions(data);
      }
    } catch (error) {
      console.error("Failed to fetch filter options:", error);
    }
  };

  useEffect(() => {
    fetchDuplicates();
  }, [fetchDuplicates]);

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
  }, []);

  const handleExportDuplicates = async () => {
    try {
      const response = await fetch(`${API}/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          export_mode: "duplicates_only",
          export_type: "csv",
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `duplicates_export_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Export completed!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Export failed");
      }
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const clearFilters = () => {
    setDuplicateType("");
    setSelectedState("");
    setPage(1);
  };

  return (
    <Layout
      title="Duplicate Intelligence Center"
      subtitle="Analyze and manage duplicate leads"
    >
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <Card className="border-border/60 bg-gradient-to-br from-orange-500/5 to-transparent">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Total Duplicates
                </p>
                <div className="text-3xl font-bold font-['Manrope'] tabular-nums">
                  {statsLoading ? (
                    <Skeleton className="h-9 w-24" />
                  ) : (
                    stats?.total_duplicates?.toLocaleString() || 0
                  )}
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
                <Copy className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Internal Duplicates
                </p>
                <div className="text-3xl font-bold font-['Manrope'] tabular-nums text-amber-500">
                  {statsLoading ? (
                    <Skeleton className="h-9 w-24" />
                  ) : (
                    stats?.internal_duplicates?.toLocaleString() || 0
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Within same upload
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <ArrowUpRight className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Historical Duplicates
                </p>
                <div className="text-3xl font-bold font-['Manrope'] tabular-nums text-red-500">
                  {statsLoading ? (
                    <Skeleton className="h-9 w-24" />
                  ) : (
                    stats?.historical_duplicates?.toLocaleString() || 0
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Already in database
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <History className="w-6 h-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-4 lg:grid-cols-2 mb-8">
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-['Manrope']">
              Duplicates by State
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : stats?.by_state?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={stats.by_state} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="state" type="category" width={50} tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar dataKey="count" fill="#f97316" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-['Manrope']">
              Duplicate Trend (30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : stats?.trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={stats.trend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#f97316"
                    strokeWidth={2}
                    dot={{ fill: "#f97316" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No trend data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Duplicate List */}
      <Card className="border-border/60">
        <CardHeader className="pb-2">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Duplicate Records
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-dup-filters-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
              <Button onClick={handleExportDuplicates} data-testid="export-duplicates-btn">
                <Download className="w-4 h-4 mr-2" />
                Export Duplicates
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          {showFilters && (
            <div className="bg-muted/30 rounded-lg p-4 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                  Duplicate Type
                </label>
                <Select value={duplicateType || "all"} onValueChange={(v) => setDuplicateType(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-dup-type">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="historical">Historical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                  State
                </label>
                <Select value={selectedState || "all"} onValueChange={(v) => setSelectedState(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-dup-state">
                    <SelectValue placeholder="All states" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All states</SelectItem>
                    {filterOptions.states.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="w-full"
                  data-testid="clear-dup-filters-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          )}

          {/* Results */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Showing <span className="font-medium text-foreground">{duplicates.length}</span> of{" "}
              <span className="font-medium text-foreground">{total.toLocaleString()}</span> duplicates
            </p>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : duplicates.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground">
                <Copy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No duplicates found</p>
                <p className="text-sm">All your leads are unique!</p>
              </div>
            ) : (
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2">Name</th>
                    <th className="text-left py-3 px-2">Phone</th>
                    <th className="text-left py-3 px-2">Email</th>
                    <th className="text-left py-3 px-2">City</th>
                    <th className="text-left py-3 px-2">State</th>
                    <th className="text-left py-3 px-2">Source</th>
                    <th className="text-center py-3 px-2">Type</th>
                    <th className="text-left py-3 px-2">Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicates.map((dup) => (
                    <tr key={dup.lead_id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-2 font-medium">
                        {dup.first_name} {dup.last_name}
                      </td>
                      <td className="py-3 px-2 tabular-nums">{dup.phone}</td>
                      <td className="py-3 px-2 text-sm truncate max-w-[200px]">{dup.email}</td>
                      <td className="py-3 px-2">{dup.city}</td>
                      <td className="py-3 px-2">{dup.state}</td>
                      <td className="py-3 px-2 text-sm">{dup.source_name}</td>
                      <td className="py-3 px-2 text-center">
                        <Badge
                          variant="outline"
                          className={
                            dup.duplicate_type === "internal"
                              ? "bg-amber-500/10 text-amber-500 border-amber-500/30"
                              : "bg-red-500/10 text-red-500 border-red-500/30"
                          }
                        >
                          {dup.duplicate_type}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-sm text-muted-foreground">
                        {new Date(dup.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {page} of {pages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="dup-prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  data-testid="dup-next-page-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
};

export default Duplicates;
